/*
  ce-ghl-tracking.js  —  Classic Exhaust customer-journey tracker for GoHighLevel
  -----------------------------------------------------------------------------
  WHAT IT DOES
  - Runs on every page (included site-wide from theme.liquid).
  - Records a behavioral journey to localStorage that survives page navigation
    within a session: page views, product views, clicks (buttons / links / CTAs
    / add-to-cart / nav / filters / search / quantity / remove), cart actions,
    form-field engagement, scroll depth and time on page.
  - When ANY request is POSTed to a GHL inbound webhook (the 6 forms already do
    this), it transparently injects three extra fields into the JSON body:
        session_id        - stable id for the visit
        session_journey   - human-readable timeline string
        tracking_events   - full raw events array (JSON string)
    => no edits to the 6 form scripts are required; the existing flow is untouched.
  - Privacy-conscious: it records WHICH fields a user engaged with, never the
    values typed (no PII) unless you explicitly add a data-track-label.

  PUBLIC API (window.CETracker)
    CETracker.track(event, details)   manually log an event
    CETracker.getEvents()             -> array of {timestamp,event,page,details}
    CETracker.getJourney()            -> readable timeline string
    CETracker.getSessionId()          -> string
    CETracker.attachTo(obj)           -> adds the 3 fields to a plain object & returns it
    CETracker.clear()                 -> clears the stored events for this session
*/
(function () {
  'use strict';
  if (window.CETracker) return;                 // singleton guard

  /* -------------------------------------------------------------- config */
  var CONFIG = {
    eventsKey:      'ghl_session_events',
    sessionKey:     'ghl_session_id',
    lastSeenKey:    'ghl_session_lastseen',
    maxEvents:      250,                         // cap to keep localStorage small
    sessionTimeout: 30 * 60 * 1000,             // 30 min of inactivity = new session
    ghlWebhook:     /leadconnectorhq\.com\/hooks\//i,           // GHL inbound webhooks
    cartEndpoint:   /\/cart\/(add|change|update)(\.js)?($|\?)/i,// AJAX cart calls
    // clicks inside these are ignored as noise
    ignoreSelector: '[data-no-track],[data-no-track] *,.shopify-section-header *:not(a):not(button),.cm_autocomplete *',
    textMax:        80
  };

  /* -------------------------------------------------------- tiny helpers */
  function now() { return new Date().toISOString(); }
  function safeParse(s, fallback) { try { return JSON.parse(s); } catch (e) { return fallback; } }
  function ls(key) { try { return window.localStorage.getItem(key); } catch (e) { return null; } }
  function lsSet(key, val) { try { window.localStorage.setItem(key, val); } catch (e) {} }
  function rand() {
    return 'sess_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 9);
  }
  function clip(str) {
    str = (str || '').replace(/\s+/g, ' ').trim();
    return str.length > CONFIG.textMax ? str.slice(0, CONFIG.textMax) + '…' : str;
  }

  /* --------------------------------------------------------- session id */
  function sessionId() {
    var id   = ls(CONFIG.sessionKey);
    var seen = parseInt(ls(CONFIG.lastSeenKey) || '0', 10);
    if (!id || (Date.now() - seen) > CONFIG.sessionTimeout) {
      id = rand();
      lsSet(CONFIG.sessionKey, id);
      lsSet(CONFIG.eventsKey, '[]');            // fresh session = fresh journey
    }
    lsSet(CONFIG.lastSeenKey, String(Date.now()));
    return id;
  }

  /* ------------------------------------------------------ event storage */
  function readEvents() { return safeParse(ls(CONFIG.eventsKey), []) || []; }
  function writeEvents(arr) {
    if (arr.length > CONFIG.maxEvents) arr = arr.slice(arr.length - CONFIG.maxEvents);
    lsSet(CONFIG.eventsKey, JSON.stringify(arr));
  }
  function track(event, details) {
    sessionId();                                // refresh "last seen"
    var arr = readEvents();
    arr.push({ timestamp: now(), event: event, page: location.pathname, details: details || {} });
    writeEvents(arr);
  }

  /* ----------------------------------------------------------- UTM grab */
  function utmParams() {
    var q = new URLSearchParams(location.search), out = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid'].forEach(function (k) {
      if (q.get(k)) out[k] = q.get(k);
    });
    return Object.keys(out).length ? out : null;
  }

  /* ------------------------------------------------- page & product view */
  function trackPageView() {
    track('page_view', {
      url:      location.pathname + location.search,
      title:    document.title,
      referrer: document.referrer || null,
      utm:      utmParams()
    });
  }
  function trackProductView() {
    var isProduct = /\/products\//.test(location.pathname) ||
                    document.querySelector('[id^="MainProduct"], product-info, .product__info-wrapper');
    if (!isProduct) return;
    var meta = (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) || {};
    var p    = meta.product || {};
    var titleEl = document.querySelector('.product__title, h1');
    var priceEl = document.querySelector('.price-item--sale, .price-item--regular, .price__regular .price-item');
    track('product_view', {
      title:   (titleEl && clip(titleEl.textContent)) || p.type || document.title,
      productId: p.id || null,
      vendor:  p.vendor || null,
      price:   priceEl ? clip(priceEl.textContent) : null,
      handle:  (location.pathname.split('/products/')[1] || '').split('?')[0] || null
    });
  }

  /* ------------------------------------------------------ click tracking */
  // classify a clicked element so the journey can flag the important stuff
  function classify(el) {
    var c = (el.className && el.className.toString() + ' ' || '') + (el.id || '');
    var t = (el.textContent || '').toLowerCase();
    var href = el.getAttribute && el.getAttribute('href') || '';
    if (/add.?to.?cart|product-form__submit|cart__checkout|btn-cart/i.test(c) || /add to cart|buy/.test(t)) return 'add_to_cart';
    if (/checkout/i.test(c + href + t)) return 'checkout';
    if (/card-product|product-card|grid__item|cer-card|crc-card/i.test(c)) return 'product_card';
    if (/quantity__button|qty/i.test(c)) return 'quantity_change';
    if (/cart-remove|cart-item__remove|remove/i.test(c)) return 'remove_from_cart';
    if (/facet|filter|cm_facet/i.test(c)) return 'filter';
    if (/search|cm_search/i.test(c)) return 'search';
    if (/header__menu|list-menu|nav|header__icon/i.test(c)) return 'navigation';
    if (/\bce-cta\b|cer-cta|crc-|button--primary|cart__checkout-button|ce-btn/i.test(c)) return 'cta';
    return el.tagName.toLowerCase() === 'a' ? 'link' : 'button';
  }
  function elDetails(el) {
    var data = {};
    if (el.dataset) for (var k in el.dataset) if (k !== 'track') data[k] = clip(el.dataset[k]);
    var flag = classify(el);
    var out = {
      flag:  flag,
      tag:   el.tagName.toLowerCase(),
      id:    el.id || null,
      cls:   el.className ? clip(el.className.toString()) : null,
      label: el.getAttribute('data-track-label') || clip(el.getAttribute('aria-label') || el.textContent),
      href:  el.getAttribute && el.getAttribute('href') || null,
      data:  Object.keys(data).length ? data : null
    };
    // product-card clicks -> capture WHICH product was clicked
    if (flag === 'product_card') {
      var card = el.closest('.card, .card-wrapper, .grid__item, .crc-card, [class*="product"]') || el;
      var nameEl = card.querySelector('.card__heading a, .card__heading, .card-information__text, h3 a, h3, .crc-card__item');
      out.product = nameEl ? clip(nameEl.textContent) : out.label;
      var link = el.matches('a[href]') ? el : card.querySelector('a[href*="/products/"]');
      if (link) out.href = link.getAttribute('href');
    }
    return out;
  }
  function onClick(e) {
    var el = e.target.closest('button, a, [role="button"], [data-track="true"], input[type="submit"]');
    if (!el) return;
    if (CONFIG.ignoreSelector && el.closest(CONFIG.ignoreSelector)) return;
    track('click', elDetails(el));
  }

  /* --------------------------------------------- form-field engagement */
  // record WHICH field was engaged (never the value -> privacy)
  function fieldInfo(t, kind) {
    if (!t || !/^(INPUT|SELECT|TEXTAREA)$/.test(t.tagName)) return null;
    if (t.type === 'password' || t.type === 'hidden') return null;
    var form = t.closest('form, .ceq-root, .cap-root, [class*="-dark"]');
    return {
      kind:  kind,
      form:  (form && (form.id || form.className.toString().split(' ')[0])) || 'form',
      field: t.getAttribute('name') || t.id || t.getAttribute('placeholder') || t.type,
      type:  t.type || t.tagName.toLowerCase()
    };
  }
  function onFocusIn(e) { var i = fieldInfo(e.target, 'focus'); if (i) track('form_field', i); }

  // a PRODUCT OPTION selection (Material / Muffler & Resonator / Clamp & Hanger, etc.)
  function variantInfo(t) {
    if (!t || !/^(INPUT|SELECT)$/.test(t.tagName)) return null;
    if (t.tagName === 'INPUT' && t.type !== 'radio') return null;   // keep radios + selects, skip qty/text
    var fs = t.closest('.product-form__input, variant-selects, .product-form__input--pill, .product-form__input--dropdown');
    if (!fs) return null;
    var legend  = fs.querySelector('legend, .form__label');
    var titleEl = document.querySelector('.product__title, h1');
    return {
      option:  legend ? clip(legend.textContent) : clip(t.name || 'option'),
      value:   clip(t.value),
      product: titleEl ? clip(titleEl.textContent) : null
    };
  }

  function onChange(e) {
    var v = variantInfo(e.target);
    if (v) { track('variant_select', v); return; }   // product option chosen
    var i = fieldInfo(e.target, 'change');
    if (i) track('form_field', i);                   // GHL form field touched
  }

  /* ------------------------------------------------------- scroll depth */
  var scrollHit = {};
  function onScroll() {
    var h = document.documentElement;
    var pct = Math.round((h.scrollTop + window.innerHeight) / h.scrollHeight * 100);
    [25, 50, 75, 100].forEach(function (m) {
      if (pct >= m && !scrollHit[m]) { scrollHit[m] = true; track('scroll_depth', { percent: m }); }
    });
  }
  var scrollTimer;
  function onScrollThrottled() { clearTimeout(scrollTimer); scrollTimer = setTimeout(onScroll, 250); }

  /* --------------------------------------------------------- time on page */
  var pageStart = Date.now();
  function trackTimeOnPage() {
    var s = Math.round((Date.now() - pageStart) / 1000);
    if (s > 1) track('time_on_page', { seconds: s });
  }

  /* --------------------------------------------- readable journey string */
  function getJourney() {
    var evts = readEvents(), out = [], first = true;
    evts.forEach(function (e) {
      var d = e.details || {}, line = null;
      switch (e.event) {
        case 'page_view':
          var where = d.title || d.url;
          line = (first ? 'Landed on ' : 'Viewed ') + where + (d.utm && d.utm.utm_source ? ' (src: ' + d.utm.utm_source + ')' : '');
          first = false; break;
        case 'product_view':   line = 'Viewed product: ' + (d.title || ''); break;
        case 'variant_select': line = 'Selected ' + (d.option || 'option') + ': ' + (d.value || ''); break;
        case 'cart_add':       line = 'Added to cart: ' + (d.title || 'item') + (d.variant && d.variant !== 'Default Title' ? ' (' + d.variant + ')' : ''); break;
        case 'cart_update':    line = 'Changed cart quantity'; break;
        case 'cart_remove':    line = 'Removed item from cart'; break;
        case 'search':         line = 'Searched: "' + (d.query || '') + '"'; break;
        case 'form_submit':    line = 'Submitted: ' + (d.form || 'form'); break;
        case 'click':
          if (d.flag === 'product_card') line = 'Clicked product: ' + (d.product || d.label || '');
          else if (['add_to_cart', 'checkout', 'cta', 'filter', 'remove_from_cart'].indexOf(d.flag) > -1)
            line = 'Clicked ' + (d.flag.replace(/_/g, ' ')) + (d.label ? ': ' + d.label : '');
          break;                                 // skip generic link/nav clicks in the readable line
      }
      if (line) out.push(line);
    });
    return out.join('  →  ');
  }

  /* ----------------------------------------- attach to an outgoing payload */
  function attachTo(obj) {
    if (!obj || typeof obj !== 'object') return obj;
    obj.session_id      = sessionId();
    obj.session_journey = getJourney();
    obj.tracking_events = JSON.stringify(readEvents());
    return obj;
  }

  /* =====================================================================
     FETCH INTERCEPTOR
     - GHL webhook POSTs  -> inject session_id / session_journey / tracking_events
       into the JSON body, then clear the journey after a successful send.
     - Shopify AJAX cart  -> auto-log add / change / update with product info.
     This is what lets the 6 existing forms gain rich data with NO code changes.
     ===================================================================== */
  var nativeFetch = window.fetch ? window.fetch.bind(window) : null;
  if (nativeFetch) {
    window.fetch = function (input, init) {
      var url = (typeof input === 'string') ? input : (input && input.url) || '';
      init = init || {};

      // --- enrich GHL webhook submissions ---
      var isGhl = CONFIG.ghlWebhook.test(url);
      if (isGhl && init.body && typeof init.body === 'string') {
        var body = safeParse(init.body, null);
        if (body && typeof body === 'object') {
          attachTo(body);
          try { track('form_submit', { form: body.formType || body.form || 'form' }); } catch (e) {}
          init.body = JSON.stringify(body);
        }
      }

      var p = nativeFetch(input, init);

      // --- log cart actions from the response ---
      if (CONFIG.cartEndpoint.test(url)) {
        p.then(function (res) {
          res.clone().json().then(function (j) {
            if (/\/cart\/add/i.test(url))    track('cart_add',    { title: j && (j.product_title || j.title), variant: j && j.variant_title, price: j && j.price, qty: j && j.quantity });
            else                              track('cart_update', { total: j && j.item_count });
          }).catch(function () {});
        }).catch(function () {});
      }

      // --- after a successful GHL submit, archive + clear the journey ---
      if (isGhl) {
        p.then(function (res) {
          if (res && res.ok) { lsSet('ghl_last_journey', JSON.stringify(readEvents())); lsSet(CONFIG.eventsKey, '[]'); }
        }).catch(function () {});
      }
      return p;
    };
  }

  /* ----------------------------------------------- search-submit capture */
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (f && (f.action || '').indexOf('/search') > -1) {
      var q = f.querySelector('input[type="search"], input[name="q"]');
      if (q && q.value) track('search', { query: clip(q.value) });
    }
  }, true);

  /* --------------------------------------------------------------- init */
  function init() {
    sessionId();
    trackPageView();
    trackProductView();
    document.addEventListener('click', onClick, true);
    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('change', onChange, true);
    window.addEventListener('scroll', onScrollThrottled, { passive: true });
    window.addEventListener('pagehide', trackTimeOnPage);
    document.addEventListener('visibilitychange', function () { if (document.hidden) trackTimeOnPage(); });
  }

  /* ------------------------------------------------------- public export */
  window.CETracker = {
    track:        track,
    getEvents:    readEvents,
    getJourney:   getJourney,
    getSessionId: sessionId,
    attachTo:     attachTo,
    clear:        function () { lsSet(CONFIG.eventsKey, '[]'); }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
